import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Pages
import Home from '../pages/Home';
import Login from '../pages/auth/Login';
import Register from '../pages/auth/Register';
import Verify from '../pages/auth/Verify';
import Profiles from '../pages/profile/Profiles';
import ProfileDetail from '../pages/profile/ProfileDetail';
import MyProfile from '../pages/profile/MyProfile';

// Admin Pages
import AdminLogin from '../pages/admin/AdminLogin';
import AdminDashboard from '../pages/admin/AdminDashboard';
import UserVerification from '../pages/admin/UserVerification';

// Loading Component
import LoadingSpinner from './common/LoadingSpinner';

const ProtectedRoute: React.FC<{ 
  children: React.ReactNode;
  requireVerified?: boolean;
}> = ({ children, requireVerified = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireVerified && user.verificationStatus !== 'verified') {
    if (user.verificationStatus === 'not-verified') {
      return <Navigate to="/verify" replace />;
    }
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAdminLoggedIn = localStorage.getItem('adminToken');
  
  if (!isAdminLoggedIn) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<{ 
  children: React.ReactNode;
  redirectIfAuthenticated?: boolean;
}> = ({ children, redirectIfAuthenticated = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (redirectIfAuthenticated && user) {
    if (user.verificationStatus === 'not-verified') {
      return <Navigate to="/verify" replace />;
    }
    return <Navigate to="/profiles" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route 
        path="/login" 
        element={
          <PublicRoute redirectIfAuthenticated>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute redirectIfAuthenticated>
            <Register />
          </PublicRoute>
        } 
      />

      {/* Protected Routes */}
      <Route 
        path="/verify" 
        element={
          <ProtectedRoute>
            <Verify />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profiles" 
        element={
          <ProtectedRoute requireVerified>
            <Profiles />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile/:id" 
        element={
          <ProtectedRoute requireVerified>
            <ProfileDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/my-profile" 
        element={
          <ProtectedRoute>
            <MyProfile />
          </ProtectedRoute>
        } 
      />

      {/* Admin Routes */}
      <Route 
        path="/admin/login" 
        element={<AdminLogin />} 
      />
      <Route 
        path="/admin/dashboard" 
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/verify" 
        element={
          <AdminRoute>
            <UserVerification />
          </AdminRoute>
        } 
      />

      {/* 404 Route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;